/**
 * Recently Viewed Products
 * Tracks product views in localStorage and renders cards client-side.
 * Shared by both the standalone recently-viewed section and the product-tabs section.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'recently-viewed-products';
  var MAX_ITEMS = 12;

  /**
   * Get stored products from localStorage
   */
  function getStoredProducts() {
    try {
      var data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Save products to localStorage
   */
  function saveProducts(products) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
    } catch (e) {
      // Storage full or unavailable
    }
  }

  /**
   * Track current product — call on every product page load
   */
  function trackProduct(productData) {
    if (!productData || !productData.handle) return;

    var products = getStoredProducts();

    // Remove duplicate if exists
    products = products.filter(function (p) {
      return p.handle !== productData.handle;
    });

    // Add to front
    products.unshift(productData);

    // Trim to max
    if (products.length > MAX_ITEMS) {
      products = products.slice(0, MAX_ITEMS);
    }

    saveProducts(products);
  }

  /**
   * Format price from cents to display string
   */
  function formatMoney(cents, moneyFormat) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = moneyFormat || '${{amount}}';

    function formatWithDelimiters(number) {
      number = (number / 100.0).toFixed(2);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');
      var cents = parts[1];
      return dollars + ',' + cents;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents).split(',')[0];
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents);
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  /**
   * Get the global card image ratio from theme settings
   */
  function getGlobalImageRatio() {
    return (window.theme && window.theme.cardImageRatio) || 'portrait';
  }

  /**
   * Build a product card HTML string matching Dawn's card structure
   */
  function buildCardHTML(product, imageRatio) {
    // Use global theme setting if no explicit ratio provided
    var ratio = imageRatio || getGlobalImageRatio();
    var ratioPercent = '100%';
    if (ratio === 'portrait') {
      ratioPercent = '125%';
    } else if (ratio === 'adapt' && product.imageAspectRatio) {
      ratioPercent = (1 / product.imageAspectRatio * 100).toFixed(2) + '%';
    }
    // square stays at 100%

    var imageHTML = '';
    if (product.image) {
      imageHTML =
        '<div class="card__media">' +
        '<div class="media media--transparent media--hover-effect">' +
        '<img' +
        ' srcset="' + product.image + '&width=165 165w, ' + product.image + '&width=360 360w, ' + product.image + '&width=533 533w, ' + product.image + '&width=720 720w"' +
        ' src="' + product.image + '&width=533"' +
        ' alt="' + (product.title || '').replace(/"/g, '&quot;') + '"' +
        ' loading="lazy"' +
        ' width="533"' +
        ' height="533"' +
        ' class="motion-reduce"' +
        '>' +
        '</div>' +
        '</div>';
    }

    var priceHTML = '';
    if (product.price) {
      var moneyFormat = window.Shopify && window.Shopify.currency && window.Shopify.currency.active
        ? '€{{amount_with_comma_separator}}'
        : '${{amount}}';

      // Use theme money format if available
      if (window.theme && window.theme.moneyFormat) {
        moneyFormat = window.theme.moneyFormat;
      }

      var formattedPrice = formatMoney(product.price, moneyFormat);
      var compareHTML = '';

      if (product.compareAtPrice && product.compareAtPrice > product.price) {
        var formattedCompare = formatMoney(product.compareAtPrice, moneyFormat);
        compareHTML =
          '<span class="price-item price-item--regular">' +
          '<s>' + formattedCompare + '</s>' +
          '</span>';
        priceHTML =
          '<div class="price price--on-sale">' +
          '<div class="price__container">' +
          '<div class="price__regular">' +
          compareHTML +
          '</div>' +
          '<div class="price__sale">' +
          '<span class="price-item price-item--sale">' + formattedPrice + '</span>' +
          '</div>' +
          '</div>' +
          '</div>';
      } else {
        priceHTML =
          '<div class="price">' +
          '<div class="price__container">' +
          '<div class="price__regular">' +
          '<span class="price-item price-item--regular">' + formattedPrice + '</span>' +
          '</div>' +
          '</div>' +
          '</div>';
      }
    }

    return (
      '<li class="grid__item">' +
      '<div class="card-wrapper product-card-wrapper underline-links-hover">' +
      '<div class="card card--standard card--media" style="--ratio-percent: ' + ratioPercent + ';">' +
      '<div class="card__inner color-scheme-1 gradient ratio" style="--ratio-percent: ' + ratioPercent + ';">' +
      imageHTML +
      '</div>' +
      '<div class="card__content">' +
      '<div class="card__information">' +
      '<h3 class="card__heading h5">' +
      '<a href="' + product.url + '" class="full-unstyled-link">' +
      (product.title || '').replace(/</g, '&lt;').replace(/>/g, '&gt;') +
      '</a>' +
      '</h3>' +
      '<div class="card-information">' +
      priceHTML +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '</li>'
    );
  }

  /**
   * Render recently viewed products into a container
   */
  function renderRecentlyViewed(container, options) {
    if (!container) return;

    var opts = options || {};
    var currentHandle = opts.currentHandle || '';
    var limit = opts.limit || 8;
    var imageRatio = opts.imageRatio || getGlobalImageRatio();
    var columns = opts.columns || 4;
    var columnsMobile = opts.columnsMobile || '2';
    var emptyMessage = opts.emptyMessage || 'No recently viewed products';

    var products = getStoredProducts();

    // Filter out current product
    if (currentHandle) {
      products = products.filter(function (p) {
        return p.handle !== currentHandle;
      });
    }

    // Limit
    products = products.slice(0, limit);

    if (products.length === 0) {
      container.innerHTML =
        '<p class="recently-viewed__empty">' + emptyMessage + '</p>';
      return;
    }

    var gridHTML =
      '<ul class="grid product-grid grid--' + columns + '-col-desktop grid--' + columnsMobile + '-col-tablet-down" role="list">';

    products.forEach(function (product) {
      gridHTML += buildCardHTML(product, imageRatio);
    });

    gridHTML += '</ul>';
    container.innerHTML = gridHTML;
  }

  // Expose globally
  window.RecentlyViewed = {
    track: trackProduct,
    render: renderRecentlyViewed,
    getProducts: getStoredProducts
  };
})();
